import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { AlertCircle, Loader2 } from "lucide-react";
import type { Account } from "../../interface/Account";
import type { AccountStatement } from "../../interface/AccountStatement";
import { portfolioService } from "../../services/portfolioService";

interface AddStatementProps {
  open: boolean;
  onClose: () => void;
  accounts: Account[];
  userId?: string;
}

const PERIOD_TYPES = [
  { value: "DAILY", label: "Daily" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "YEARLY", label: "Yearly" },
];

export function AddStatement({ open, onClose, accounts, userId }: AddStatementProps) {
  const [form, setForm] = useState({
    accountId: "",
    statementDate: "",
    startDate: "",
    endDate: "",
    periodType: "MONTHLY",
    startingValue: "",
    endingValue: "",
    deposits: "",
    withdrawals: "",
    fees: "",
    dividendsEarned: "",
    interestEarned: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [statements, setStatements] = useState<AccountStatement[]>([]);
  const [isLoadingStatements, setIsLoadingStatements] = useState(false);
  const [statementsError, setStatementsError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !userId) return;

    const fetchStatements = async () => {
      setIsLoadingStatements(true);
      setStatementsError(null);
      try {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 6);
        const request =  {
          accountId: "", // Fetch for all accounts
          startDate: startDate.toISOString().split("T")[0],
          endDate: endDate.toISOString().split("T")[0],
          periodType: "MONTHLY"
        };
        const response = await portfolioService.getAllAccountStatements(userId, request) as AccountStatement[] | { data?: AccountStatement[] };
        const data = Array.isArray(response) ? response : (response.data || []);
        setStatements(
          data.sort((a: AccountStatement, b: AccountStatement) =>
            b.statementDate.localeCompare(a.statementDate)
          )
        );
      } catch (error) {
        setStatementsError(error instanceof Error ? error.message : "Failed to load statements");
      } finally {
        setIsLoadingStatements(false);
      }
    };

    fetchStatements();
  }, [open, userId]);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm({
      accountId: "",
      statementDate: "",
      startDate: "",
      endDate: "",
      periodType: "MONTHLY",
      startingValue: "",
      endingValue: "",
      deposits: "",
      withdrawals: "",
      fees: "",
      dividendsEarned: "",
      interestEarned: "",
    });
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!form.accountId || !form.statementDate || !form.startDate || !form.endDate || !form.startingValue || !form.endingValue) {
      setSaveError("Please fill in all required fields");
      return;
    }

    const startingValue = parseFloat(form.startingValue);
    const endingValue = parseFloat(form.endingValue);
    const deposits = parseFloat(form.deposits) || 0;
    const withdrawals = parseFloat(form.withdrawals) || 0;
    const fees = parseFloat(form.fees) || 0;
    const dividendsEarned = parseFloat(form.dividendsEarned) || 0;
    const interestEarned = parseFloat(form.interestEarned) || 0;

    const netDeposits = deposits - withdrawals - fees + dividendsEarned + interestEarned;
    const netReturn = ((endingValue - startingValue) / startingValue) * 100;
    const timeWeightedReturn = startingValue > 0 ? ((endingValue - netDeposits) / startingValue - 1) * 100 : 0;

    const dto = {
      accountId: form.accountId,
      statementDate: form.statementDate,
      startDate: form.startDate,
      endDate: form.endDate,
      periodType: form.periodType,
      startingValue,
      endingValue,
      deposits,
      withdrawals,
      fees,
      dividendsEarned,
      interestEarned,
      netReturn: Math.round(netReturn * 100) / 100,
      timeWeightedReturn: Math.round(timeWeightedReturn * 100) / 100,
      moneyWeightedReturn: Math.round(timeWeightedReturn * 100) / 100,
    };

    try {
      setIsSaving(true);
      setSaveError(null);
      await portfolioService.createAccountStatement(userId, dto);
      resetForm();
      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to save statement");
    } finally {
      setIsSaving(false);
    }
  };

  const recentStatements = statements.slice(0, 6);

  return (
    <Dialog open={open} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Account Statement</DialogTitle>
          <DialogDescription>
            Enter account statement details for a specific period
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="accountId">Account *</Label>
              <Select value={form.accountId} onValueChange={(value) => handleChange("accountId", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.accountNumberCustodian || account.accountNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="periodType">Period Type *</Label>
              <Select value={form.periodType} onValueChange={(value) => handleChange("periodType", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  {PERIOD_TYPES.map((pt) => (
                    <SelectItem key={pt.value} value={pt.value}>
                      {pt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="statementDate">Statement Date *</Label>
              <Input
                id="statementDate"
                type="date"
                value={form.statementDate}
                onChange={(e) => handleChange("statementDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date *</Label>
              <Input
                id="startDate"
                type="date"
                value={form.startDate}
                onChange={(e) => handleChange("startDate", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endDate">End Date *</Label>
              <Input
                id="endDate"
                type="date"
                value={form.endDate}
                onChange={(e) => handleChange("endDate", e.target.value)}
              />
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium mb-3">Statement Values</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startingValue">Starting Value *</Label>
                <Input
                  id="startingValue"
                  type="number"
                  placeholder="0.00"
                  value={form.startingValue}
                  onChange={(e) => handleChange("startingValue", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endingValue">Ending Value *</Label>
                <Input
                  id="endingValue"
                  type="number"
                  placeholder="0.00"
                  value={form.endingValue}
                  onChange={(e) => handleChange("endingValue", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="deposits">Deposits</Label>
                <Input
                  id="deposits"
                  type="number"
                  placeholder="0.00"
                  value={form.deposits}
                  onChange={(e) => handleChange("deposits", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="withdrawals">Withdrawals</Label>
                <Input
                  id="withdrawals"
                  type="number"
                  placeholder="0.00"
                  value={form.withdrawals}
                  onChange={(e) => handleChange("withdrawals", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="fees">Fees</Label>
                <Input
                  id="fees"
                  type="number"
                  placeholder="0.00"
                  value={form.fees}
                  onChange={(e) => handleChange("fees", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dividendsEarned">Dividends Earned</Label>
                <Input
                  id="dividendsEarned"
                  type="number"
                  placeholder="0.00"
                  value={form.dividendsEarned}
                  onChange={(e) => handleChange("dividendsEarned", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="interestEarned">Interest Earned</Label>
                <Input
                  id="interestEarned"
                  type="number"
                  placeholder="0.00"
                  value={form.interestEarned}
                  onChange={(e) => handleChange("interestEarned", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-medium mb-3">Recent Statements (Last 6 Months)</h3>
            <div className="border rounded-lg overflow-hidden">
              {isLoadingStatements ? (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading statements...
                </div>
              ) : statementsError ? (
                <div className="p-4 text-sm text-red-600">{statementsError}</div>
              ) : recentStatements.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  No statements found
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account</TableHead>
                    <TableHead>Period</TableHead>
                    <TableHead>Statement <br/>Date</TableHead>
                    <TableHead className="text-right">Start Value</TableHead>
                    <TableHead className="text-right">End Value</TableHead>
                    <TableHead className="text-right">Return</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentStatements.map((stmt) => {
                    const account = accounts.find((a) => a.id === stmt.accountId);
                    const hasValues = stmt.startingValue != null && stmt.endingValue != null;
                    const netDeposits = hasValues
                      ? stmt.deposits - stmt.withdrawals - stmt.fees + stmt.dividendsEarned + stmt.interestEarned
                      : 0;
                    const twr = hasValues && stmt.startingValue > 0
                      ? (stmt.source === "PLAID"
                          ? ((stmt.endingValue - stmt.startingValue) / stmt.startingValue) * 100
                          : ((stmt.endingValue - netDeposits) / stmt.startingValue - 1) * 100)
                      : 0;
                    return (
                      <TableRow key={stmt.id}>
                        <TableCell className="font-medium">
                          {account?.accountNumberCustodian || stmt.accountId}
                        </TableCell>
                        <TableCell>
                          {stmt.periodType === "MONTHLY" ? "Monthly" : stmt.periodType === "DAILY" ? "Daily" : "Yearly"}
                        </TableCell>
                        <TableCell>
                          {new Date(stmt.statementDate).toLocaleDateString("en-US", {
                            month: "short",
                            year: "numeric",
                          })}
                        </TableCell>
                        <TableCell className="text-right">
                          {stmt.startingValue != null ? `$${stmt.startingValue.toLocaleString()}` : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {stmt.endingValue != null ? `$${stmt.endingValue.toLocaleString()}` : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className={twr >= 0 ? "text-green-600" : "text-red-600"}>
                            {twr >= 0 ? "+" : ""}
                            {twr.toFixed(2)}%
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              )}
            </div>
          </div>
        </div>

        {saveError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{saveError}</span>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Statement"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
